import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';

export const isGuestUserRef = (userRef: string | null | undefined): boolean => {
  const ref = userRef?.toLowerCase() ?? '';
  return ref === '' || ref.endsWith('/guest');
};

export type GuestState = {
  isGuest: boolean;
  loading: boolean;
};

const GuestContext = createContext<GuestState | undefined>(undefined);

export const GuestProvider = ({ children }: PropsWithChildren<{}>) => {
  const identityApi = useApi(identityApiRef);
  const [state, setState] = useState<GuestState>({
    isGuest: true,
    loading: true,
  });

  useEffect(() => {
    let mounted = true;

    identityApi
      .getBackstageIdentity()
      .then(identity => isGuestUserRef(identity?.userEntityRef))
      .catch(() => true)
      .then(isGuest => {
        if (mounted) {
          setState({ isGuest, loading: false });
        }
      });

    return () => {
      mounted = false;
    };
  }, [identityApi]);

  return (
    <GuestContext.Provider value={state}>{children}</GuestContext.Provider>
  );
};

export const useGuestState = (): GuestState => {
  const state = useContext(GuestContext);
  if (!state) {
    throw new Error('useGuestState must be used within a GuestProvider');
  }
  return state;
};

export const useIsGuest = () => useGuestState().isGuest;
